import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { money } from "../lib/shop";

const Box = ({ children, testid }) => <section className="bg-[#FFF4F8] px-4 py-20"><div className="stitch-box relative mx-auto max-w-xl rounded-[2rem] bg-white p-10 text-center" data-testid={testid}>{children}</div></section>;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  useEffect(() => {
    let tries = 0, timer;
    const poll = () => api.get(`/payments/status/${params.get("session_id")}`).then((r) => {
      if (r.data.payment_status === "paid") return setState("paid");
      if (r.data.status === "expired" || ++tries > 10) return setState("pending");
      timer = setTimeout(poll, 2000);
    }).catch(() => setState("pending"));
    poll();
    return () => clearTimeout(timer);
  }, [params]);
  if (state === "checking") return <Box testid="payment-checking"><Loader2 className="mx-auto h-10 w-10 animate-spin text-[#FF1F8E]" /><p className="mt-4">Confirming your payment…</p></Box>;
  return (
    <Box testid={state === "paid" ? "payment-success" : "payment-pending"}>
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#FF1F8E]" />
      <p className="mt-4 font-script text-5xl text-[#FF1F8E]">{state === "paid" ? "Thank you!" : "Almost there"}</p>
      <h1 className="mt-2 font-display text-2xl font-bold">{state === "paid" ? "Your payment is received and your order is confirmed." : "We're still confirming your payment. You'll get an email shortly."}</h1>
      <p className="mt-3 text-black/60">A confirmation email is on its way.</p>
      <Link to="/" className="btn-pink mt-8">Back to Home</Link>
    </Box>
  );
};

export const PaymentCancel = () => (
  <Box testid="payment-cancel">
    <h1 className="font-display text-2xl font-bold">Payment cancelled</h1>
    <p className="mt-3 text-black/60">No money was taken. You can try again any time.</p>
    <Link to="/shop" className="btn-pink mt-8">Back to Shop</Link>
  </Box>
);

export const PayQuote = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/pay/${id}`, { params: { t: params.get("t") } }).then((r) => setInfo(r.data)).catch(() => setInfo(false)); }, [id, params]);
  const pay = async () => {
    setBusy(true);
    try { window.location.href = (await api.post(`/pay/${id}`, { t: params.get("t"), origin_url: window.location.origin })).data.checkout_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  if (info === null) return <Box testid="pay-loading"><Loader2 className="mx-auto h-8 w-8 animate-spin" /></Box>;
  if (!info) return <Box testid="pay-invalid"><h1 className="font-display text-2xl font-bold">This payment link isn't valid.</h1><p className="mt-3 text-black/60">Please contact us for a new one.</p></Box>;
  if (info.paid) return <Box testid="pay-already-paid"><CheckCircle2 className="mx-auto h-12 w-12 text-[#FF1F8E]" /><h1 className="mt-3 font-display text-2xl font-bold">This order is already paid. Thank you!</h1></Box>;
  return (
    <Box testid="pay-quote">
      <p className="font-script text-4xl text-[#FF1F8E]">Hi {info.name},</p>
      <h1 className="mt-2 font-display text-2xl font-bold">Your custom order: {info.product_type}</h1>
      <p className="mt-6 font-display text-5xl font-black" data-testid="pay-amount">{money(info.final_price)}</p>
      <p className="mt-1 text-xs text-black/50">Sales tax, if any, is added at checkout.</p>
      <button onClick={pay} disabled={busy} className="btn-pink mt-8 w-full !py-4 text-lg disabled:opacity-60" data-testid="pay-now-btn">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}Pay Now</button>
      <p className="mt-3 text-xs text-black/50">Secure checkout by Stripe · Card, Apple Pay & Google Pay</p>
    </Box>
  );
};
